import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ShoppingCartIcon, 
  PlusIcon, 
  MinusIcon,
  XMarkIcon,
  TrashIcon,
  ArrowLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'
import useCartStore from '../../stores/useCartStore'
import { supabase } from '../../config/supabase'
import toast from 'react-hot-toast'
import ordyrrCoinImage from '../../assets/ordyrr coin.png'
import OfferService from '../../services/offerService'

// Ordyrr Brand Colors
const BRAND_GREEN = '#00E676'
const ACTION_GREEN = '#00C853'
const DARK_TEXT = '#3C3C3C'
const MEDIUM_GRAY = '#9E9E9E'

const CartSidebar = ({ isOpen, onClose, onCheckout, selectedTip = 0, currentCustomer, isAuthenticated, restaurantId, allMenuItems = [], sessionId = null }) => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, getCartCount, addToCart } = useCartStore()
  const [hasUsedFirstOrderDiscount, setHasUsedFirstOrderDiscount] = useState(false)
  const [showDiscountPopup, setShowDiscountPopup] = useState(false)
  const [currentOffer, setCurrentOffer] = useState(null)
  const [useOrdyrrCoins, setUseOrdyrrCoins] = useState(false)
  const [availableCoins, setAvailableCoins] = useState(0)
  const [tipAmount, setTipAmount] = useState(0)
  const [customTip, setCustomTip] = useState('')
  const [showCustomTip, setShowCustomTip] = useState(false)
  const [showCouponSection, setShowCouponSection] = useState(false)
  const [showBillDetails, setShowBillDetails] = useState(false)
  const [showSparkles, setShowSparkles] = useState(false)
  const [eligibleOffers, setEligibleOffers] = useState([])
  const [appliedOffer, setAppliedOffer] = useState(null)
  const [loadingOffers, setLoadingOffers] = useState(false)
  const hasShownPopup = useRef(false)
  const popupTimeout = useRef(null)
  const previousSubtotal = useRef(0)

  // Fetch customer's Ordyrr Coins balance
  useEffect(() => {
    const fetchCoinsBalance = async () => {
      if (currentCustomer?.id) {
        try {
          console.log('💰 [CartSidebar] Fetching GLOBAL coins for customer:', currentCustomer.id)
          const { data, error } = await supabase
            .from('loyalty_points')
            .select('points_earned, points_redeemed')
            .eq('customer_id', currentCustomer.id)
            // Removed restaurant_id filter - coins are now global across all restaurants
          
          if (error) {
            console.error('❌ Error fetching coins:', error)
            return
          }
          
          if (data) {
            // Calculate total points: sum of all earned points minus redeemed points across ALL restaurants
            const totalCoins = (data || []).reduce((total, transaction) => {
              const earned = transaction.points_earned || 0
              const redeemed = transaction.points_redeemed || 0
              return total + earned - redeemed
            }, 0)
            setAvailableCoins(totalCoins)
            console.log('✅ [CartSidebar] Available GLOBAL Ordyrr Coins:', totalCoins, 'from', data.length, 'transactions across all restaurants')
          }
        } catch (error) {
          console.error('❌ Exception fetching coins balance:', error)
        }
      }
    }
    fetchCoinsBalance()
  }, [currentCustomer])

  // Check if customer has used the first order offer
  useEffect(() => {
    const checkOfferEligibility = async () => {
      if (currentCustomer?.id) {
        try {
          console.log('🔍 Checking offer eligibility for customer:', currentCustomer.id)
          
          // Calculate current subtotal inside useEffect to avoid initialization error
          const currentSubtotal = getCartTotal()
          
          // Fetch both first order offers
          const { data: offers, error: offerError } = await supabase
            .from('offers')
            .select('*')
            .in('offer_code', ['FIRST_ORDER_10', 'FIRST_ORDER_25'])
            .eq('is_active', true)
          
          if (offerError || !offers || offers.length === 0) {
            console.log('❌ First order offers not found or inactive')
            return
          }
          
          console.log('✅ Found offers:', offers.map(o => o.offer_code))
          
          // Determine which offer to show based on subtotal
          // If subtotal >= 499, show FIRST_ORDER_10 (10% off, max ₹50)
          // If subtotal >= 299 and < 499, show FIRST_ORDER_25 (flat ₹25 off)
          const offer10 = offers.find(o => o.offer_code === 'FIRST_ORDER_10')
          const offer25 = offers.find(o => o.offer_code === 'FIRST_ORDER_25')
          
          let selectedOffer = null
          if (currentSubtotal >= 499 && offer10) {
            selectedOffer = offer10
          } else if (currentSubtotal >= 299 && currentSubtotal < 499 && offer25) {
            selectedOffer = offer25
          } else if (currentSubtotal >= 499 && !offer10 && offer25) {
            // Fallback: if FIRST_ORDER_10 doesn't exist but subtotal is high enough
            selectedOffer = offer25
          }
          
          if (selectedOffer) {
            console.log('✅ Selected offer:', selectedOffer.offer_code, 'for subtotal:', currentSubtotal)
            setCurrentOffer(selectedOffer)
          } else {
            console.log('⚠️ No offer matches current subtotal:', currentSubtotal)
            setCurrentOffer(null)
          }
          
          // IMPORTANT: Check if customer has placed ANY order before (not just used the offer)
          // First order discount is ONLY for the actual first order
          const { data: orderHistory, error: orderError } = await supabase
            .from('orders')
            .select('id')
            .eq('customer_id', currentCustomer.id)
            .limit(1)
          
          console.log('📊 Customer order history check:', {
            customer_id: currentCustomer.id,
            hasPlacedOrder: orderHistory && orderHistory.length > 0,
            orderError
          })
          
          if (!orderError && orderHistory && orderHistory.length > 0) {
            // Customer has already placed at least one order
            // First order discount is NO LONGER available
            console.log('🚫 Customer has already placed an order - First order discount not available')
            setHasUsedFirstOrderDiscount(true)
          } else {
            // This is the customer's first order - discount is available
            console.log('✅ Customer is placing their FIRST order - First order discount available')
            setHasUsedFirstOrderDiscount(false)
          }
        } catch (error) {
          console.error('❌ Error checking offer eligibility:', error)
        }
      }
    }
    checkOfferEligibility()
  }, [currentCustomer, cart])

  // Fetch all eligible offers for the customer
  useEffect(() => {
    const fetchEligibleOffers = async () => {
      if (!isOpen) return // Only fetch when cart is open
      
      setLoadingOffers(true)
      try {
        const subtotal = getCartTotal()
        const customerId = currentCustomer?.id || null
        const customerPhone = currentCustomer?.phone || null
        
        console.log('🎯 Fetching eligible offers for customer:', { customerId, sessionId, customerPhone, subtotal })
        
        const offers = await OfferService.getEligibleOffers(customerId, subtotal, sessionId, customerPhone)
        
        console.log('✅ Eligible offers found:', offers.length, offers.map(o => o.offer_code))
        setEligibleOffers(offers)
      } catch (error) {
        console.error('❌ Error fetching eligible offers:', error)
        setEligibleOffers([])
      } finally {
        setLoadingOffers(false)
      }
    }
    
    fetchEligibleOffers()
  }, [isOpen, currentCustomer, cart, getCartTotal])

  const subtotal = getCartTotal()
  
  // Calculate savings from menu item discounts
  // Savings = menu_display_price (marked up) - platform_price (actual selling price)
  const itemSavings = cart.reduce((savings, item) => {
    const displayPrice = item.menu_display_price // Higher marked-up price
    const sellingPrice = item.platform_price // Actual selling price
    
    if (displayPrice && sellingPrice && displayPrice > sellingPrice) {
      const itemSaving = (displayPrice - sellingPrice) * item.quantity
      return savings + itemSaving
    }
    return savings
  }, 0)
  
  // RULE: Only ONE offer can be applied at a time
  // Priority: Ordyrr Coins > Applied Database Offer > First Order Discount > Other Coupons
  
  let discount = 0
  let coinsDiscount = 0
  let coinsUsed = 0
  
  // Check if Ordyrr Coins are being used (highest priority)
  if (useOrdyrrCoins && isAuthenticated && availableCoins > 0 && subtotal >= 100) {
    // Calculate Ordyrr Coins discount
    // Max discount is 5% of subtotal
    const maxCoinsDiscount = subtotal * 0.05
    // Max 500 coins per order (₹50)
    const maxCoinsAllowed = 500
    // Use exact number of coins available (up to max allowed)
    coinsUsed = Math.min(availableCoins, maxCoinsAllowed)
    // Convert coins to exact rupee value (1 coin = ₹0.10, so 10 coins = ₹1)
    const coinsValue = coinsUsed / 10
    // Take the minimum of coins value and max allowed discount (5%)
    coinsDiscount = Math.min(coinsValue, maxCoinsDiscount)
    // Recalculate exact coins used based on final discount (no rounding)
    coinsUsed = coinsDiscount * 10
    
    // If using Ordyrr Coins, NO other offers can be applied
    discount = 0
  } else if (appliedOffer && subtotal >= (appliedOffer.min_order_amount || 0)) {
    // Calculate discount from applied database offer (second priority)
    discount = OfferService.calculateDiscount(appliedOffer, subtotal)
    
    // If using database offer, NO Ordyrr Coins can be applied
    coinsDiscount = 0
    coinsUsed = 0
  } else if (isAuthenticated && !hasUsedFirstOrderDiscount && currentOffer && subtotal >= (currentOffer.min_order_amount || 0)) {
    // Calculate First Order Discount (only if NOT using Ordyrr Coins or database offer)
    if (currentOffer.discount_type === 'percentage') {
      discount = subtotal * (currentOffer.discount_value / 100)
      // Apply max discount cap if specified
      if (currentOffer.max_discount_amount) {
        discount = Math.min(discount, currentOffer.max_discount_amount)
      }
    } else if (currentOffer.discount_type === 'fixed') {
      discount = currentOffer.discount_value
    }
    
    // If using First Order Discount, NO Ordyrr Coins can be applied
    coinsDiscount = 0
    coinsUsed = 0
  }
  
  // Calculate total with tip (no platform fee)
  const totalSavings = discount + coinsDiscount
  const total = subtotal - discount - coinsDiscount + tipAmount
  
  // Calculate original total (with menu_display_price instead of platform_price) for strikethrough display
  const originalSubtotal = cart.reduce((sum, item) => {
    const displayPrice = item.menu_display_price || item.platform_price || item.price
    return sum + (displayPrice * item.quantity)
  }, 0)
  const originalTotal = originalSubtotal - discount - coinsDiscount + tipAmount
  
  // Reset popup flag when cart sidebar opens
  useEffect(() => {
    if (isOpen) {
      hasShownPopup.current = false
    }
  }, [isOpen])

  // Show celebratory popup when discount is applied OR when user crosses threshold
  useEffect(() => {
    const currentSubtotal = subtotal
    const prevSubtotal = previousSubtotal.current
    
    // Check if user just crossed a threshold (299 or 499)
    const crossedThreshold = 
      (prevSubtotal < 299 && currentSubtotal >= 299) || 
      (prevSubtotal < 499 && currentSubtotal >= 499)
    
    // Show popup if:
    // 1. Discount is applied and popup hasn't been shown yet, OR
    // 2. User just crossed a threshold by adding items
    if (isOpen && isAuthenticated && !hasUsedFirstOrderDiscount) {
      if ((discount > 0 && !hasShownPopup.current) || crossedThreshold) {
        hasShownPopup.current = true
        setShowDiscountPopup(true)
        // Auto-dismiss after 4 seconds
        if (popupTimeout.current) {
          clearTimeout(popupTimeout.current)
        }
        popupTimeout.current = setTimeout(() => {
          setShowDiscountPopup(false)
        }, 4000)
      }
    }
    
    // Update previous subtotal
    previousSubtotal.current = currentSubtotal
  }, [discount, subtotal, isOpen, isAuthenticated, hasUsedFirstOrderDiscount])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (popupTimeout.current) {
        clearTimeout(popupTimeout.current)
      }
    }
  }, [])

  // Get suggested items (items not in cart)
  const cartItemIds = cart.map(item => item.id)
  const suggestedItems = allMenuItems
    .filter(item => !cartItemIds.includes(item.id) && item.is_available)
    .slice(0, 6)

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Cart Sidebar */}
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full sm:max-w-md bg-gray-50 z-50 shadow-2xl flex flex-col overflow-hidden"
          >

            {/* Header - Clean White Style */}
            <div className="bg-white border-b border-gray-200">
              <div className="p-4 flex items-center gap-4">
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center"
                >
                  <ArrowLeftIcon className="w-6 h-6" style={{ color: DARK_TEXT }} />
                </motion.button>
                <h2 className="text-xl font-bold" style={{ color: DARK_TEXT }}>Your Cart</h2>
              </div>
            </div>

            {/* Cart Items - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              {cart.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-16 px-4 bg-white"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.4 }}
                    className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, ${ACTION_GREEN} 100%)` }}
                  >
                    <ShoppingCartIcon className="w-12 h-12 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-black mb-2" style={{ color: DARK_TEXT }}>Your cart is empty</h3>
                  <p className="mb-6" style={{ color: MEDIUM_GRAY }}>Add some delicious items to get started!</p>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={onClose}
                    className="px-8 py-3 text-black rounded-xl font-bold"
                    style={{ 
                      backgroundColor: ACTION_GREEN,
                      boxShadow: '0 4px 0 0 #000000'
                    }}
                  >
                    Browse Menu
                  </motion.button>
                </motion.div>
              ) : (
                <>
                  {/* Merged Savings Banner and Apply Coupon Section */}
                  <div className="mx-4 mt-4 rounded-2xl overflow-hidden" style={{ boxShadow: '0px 2px 8px rgba(0,0,0,0.06)' }}>
                    {/* Savings Banner */}
                    {totalSavings > 0 && (
                      <div className="p-4 relative" style={{ backgroundColor: '#E8F5E9' }}>
                        {/* Sparkles Animation */}
                        <AnimatePresence>
                          {showSparkles && (
                            <>
                              {[...Array(20)].map((_, i) => {
                                // Random positions spread across full banner width
                                const startLeft = Math.random() * 100 // 0% to 100% across width
                                const startTop = Math.random() * 100 // 0% to 100% across height
                                const endTop = startTop - 30 - Math.random() * 20 // Float upward
                                
                                return (
                                  <motion.div
                                    key={i}
                                    initial={{ 
                                      left: `${startLeft}%`,
                                      top: `${startTop}%`,
                                      scale: 0, 
                                      opacity: 0,
                                      rotate: 0
                                    }}
                                    animate={{
                                      top: `${endTop}%`,
                                      scale: [0, 1.2, 0.8, 0],
                                      opacity: [0, 1, 1, 0],
                                      rotate: [0, 180, 360]
                                    }}
                                    transition={{
                                      duration: 1,
                                      delay: i * 0.05,
                                      ease: "easeOut"
                                    }}
                                    className="absolute"
                                    style={{ pointerEvents: 'none' }}
                                  >
                                    <span className="text-yellow-400 text-xl">✨</span>
                                  </motion.div>
                                )
                              })}
                            </>
                          )}
                        </AnimatePresence>
                        
                        <p className="text-base font-normal leading-relaxed" style={{ color: '#43A047' }}>
                          <span className="font-semibold text-lg">₹{totalSavings.toFixed(2)} savings</span> on this order, including{' '}
                          <span className="font-semibold text-lg">₹{(discount + coinsDiscount).toFixed(2)}</span> with Ordyrr!
                        </p>
                      </div>
                    )}

                    {/* Apply Coupon Button */}
                    <button
                      onClick={() => setShowCouponSection(!showCouponSection)}
                      className="w-full px-5 py-4 flex items-center justify-between bg-white"
                    >
                      <div className="text-left">
                        <h3 className="font-semibold text-lg mb-1" style={{ color: DARK_TEXT }}>Apply Coupon</h3>
                        <p className="text-sm" style={{ color: MEDIUM_GRAY }}>
                          {totalSavings > 0 ? `Saving ₹${totalSavings.toFixed(0)}` : 'Save more with coupons available for you'}
                        </p>
                      </div>
                      <motion.div
                        animate={{ rotate: showCouponSection ? 90 : 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      >
                        <ChevronRightIcon className="w-6 h-6" style={{ color: '#BDBDBD' }} />
                      </motion.div>
                    </button>

                    {/* Expandable Coupon List */}
                    <AnimatePresence>
                      {showCouponSection && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="border-t border-gray-100 overflow-hidden"
                        >
                          <div className="p-4 space-y-3">
                            {/* Ordyrr Coin Option */}
                            {isAuthenticated && availableCoins > 0 && (
                              <div className="relative bg-white rounded-lg overflow-hidden" style={{ boxShadow: '0px 2px 8px rgba(0,0,0,0.08)' }}>
                                {/* Orange Stripe with Circles */}
                                <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col items-center justify-center gap-2" style={{ backgroundColor: '#FF5722' }}>
                                  {/* Decorative Circles */}
                                  <div className="w-2 h-2 rounded-full bg-gray-50"></div>
                                  <div className="w-2 h-2 rounded-full bg-gray-50"></div>
                                  <div className="w-2 h-2 rounded-full bg-gray-50"></div>
                                  <div className="w-2 h-2 rounded-full bg-gray-50"></div>
                                </div>

                                <div className="pl-14 pr-3 py-3 flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    {/* Title with Icon */}
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                      <div className="w-5 h-5 rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#FEF3C7' }}>
                                        <img src={ordyrrCoinImage} alt="Ordyrr Coin" className="w-4 h-4 object-contain" />
                                      </div>
                                      <h4 className="font-semibold text-sm" style={{ color: DARK_TEXT }}>Ordyrr Coin</h4>
                                    </div>

                                    {/* Discount Description */}
                                    <p className="text-xs mb-2 font-medium" style={{ color: '#00897B' }}>
                                      Get flat 5% discount using Ordyrr Coin
                                    </p>

                                    {/* Dotted Divider */}
                                    <div className="border-t border-dashed border-gray-300 mb-2"></div>

                                    {/* Conditions */}
                                    <p className="text-[10px] mb-0.5" style={{ color: '#9E9E9E' }}>
                                      Applicable on Order above ₹100
                                    </p>
                                    <p className="text-[10px] mb-2" style={{ color: '#9E9E9E' }}>
                                      Max ₹ 50
                                    </p>

                                    {/* Status Messages */}
                                    {subtotal < 100 && (
                                      <p className="text-[10px] text-red-600 font-semibold">
                                        ⚠️ Minimum order ₹100 required
                                      </p>
                                    )}
                                    {useOrdyrrCoins && subtotal >= 100 && (
                                      <p className="text-[10px] font-semibold" style={{ color: '#15803D' }}>
                                        ✓ Saving ₹{coinsDiscount.toFixed(2)} ({coinsUsed} coins used)
                                      </p>
                                    )}
                                  </div>

                                  {/* Apply Button */}
                                  <button
                                    onClick={() => {
                                      setUseOrdyrrCoins(!useOrdyrrCoins)
                                      if (!useOrdyrrCoins) {
                                        // Applying Ordyrr Coins - remove any applied offer
                                        setAppliedOffer(null)
                                        toast.success('Ordyrr Coins applied!')
                                        // Show sparkles animation
                                        setShowSparkles(true)
                                        setTimeout(() => setShowSparkles(false), 1500)
                                        // Close the coupon section after applying
                                        setTimeout(() => setShowCouponSection(false), 300)
                                      } else {
                                        // Removing Ordyrr Coins
                                        toast.success('Ordyrr Coins removed')
                                      }
                                    }}
                                    disabled={subtotal < 100}
                                    className="px-4 py-1 text-xs font-bold rounded transition-all flex-shrink-0"
                                    style={{ 
                                      color: useOrdyrrCoins ? '#15803D' : '#FF5722',
                                      backgroundColor: 'transparent',
                                      opacity: subtotal < 100 ? 0.5 : 1
                                    }}
                                  >
                                    {useOrdyrrCoins ? 'APPLIED' : 'APPLY'}
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* First Order Discount - Coupon Card Style */}
                            {isAuthenticated && !hasUsedFirstOrderDiscount && currentOffer && (
                              <div className="flex rounded-lg overflow-hidden bg-white" style={{ boxShadow: '0px 2px 8px rgba(0,0,0,0.08)' }}>
                                {/* Green Sidebar with Dots */}
                                <div className="w-16 flex flex-col items-center justify-center gap-3 flex-shrink-0" style={{ backgroundColor: '#00C853' }}>
                                  <div className="w-2 h-2 rounded-full bg-white opacity-60"></div>
                                  <div className="w-2 h-2 rounded-full bg-white opacity-60"></div>
                                  <div className="w-2 h-2 rounded-full bg-white opacity-60"></div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 p-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-base">🎉</span>
                                        <h4 className="font-bold text-sm" style={{ color: DARK_TEXT }}>{currentOffer.offer_code}</h4>
                                      </div>
                                      <p className="text-xs mb-1" style={{ color: '#00796B' }}>
                                        Save {currentOffer.discount_type === 'percentage' ? `${currentOffer.discount_value}%` : `₹${currentOffer.discount_value}`} on this order
                                      </p>
                                      <p className="text-[10px] mb-2" style={{ color: '#9E9E9E' }}>
                                        Applicable on Order above ₹{currentOffer.min_order_amount || 0}
                                        <br />
                                        Max ₹{currentOffer.max_discount_amount || 0}
                                      </p>
                                      {discount > 0 && !useOrdyrrCoins && (
                                        <p className="text-[10px] font-semibold" style={{ color: '#15803D' }}>
                                          ✓ Saving ₹{discount.toFixed(2)}
                                        </p>
                                      )}
                                    </div>

                                    {/* Apply/Applied Text */}
                                    <button
                                      onClick={() => {
                                        if (!useOrdyrrCoins && discount > 0) {
                                          // Already applied, do nothing
                                          return
                                        }
                                        // Clear any applied database offer
                                        setAppliedOffer(null)
                                        setUseOrdyrrCoins(false)
                                        toast.success('First Order Discount applied!')
                                        // Show sparkles animation
                                        setShowSparkles(true)
                                        setTimeout(() => setShowSparkles(false), 1500)
                                        // Close the coupon section after applying
                                        setTimeout(() => setShowCouponSection(false), 300)
                                      }}
                                      disabled={!useOrdyrrCoins && discount > 0}
                                      className="px-4 py-1 text-xs font-bold rounded transition-all flex-shrink-0"
                                      style={{ 
                                        color: (!useOrdyrrCoins && discount > 0) ? '#15803D' : '#15803D',
                                        backgroundColor: 'transparent',
                                        cursor: (!useOrdyrrCoins && discount > 0) ? 'default' : 'pointer'
                                      }}
                                    >
                                      {!useOrdyrrCoins && discount > 0 ? 'APPLIED' : 'APPLY'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* All Eligible Offers from Database */}
                            {eligibleOffers.length > 0 && eligibleOffers.map((offer) => {
                              const offerDiscount = OfferService.calculateDiscount(offer, subtotal)
                              const isApplied = appliedOffer?.id === offer.id
                              
                              return (
                                <div key={offer.id} className="relative bg-white rounded-lg overflow-hidden" style={{ boxShadow: '0px 2px 8px rgba(0,0,0,0.08)' }}>
                                  {/* Colored Stripe with Circles */}
                                  <div 
                                    className="absolute left-0 top-0 bottom-0 w-12 flex flex-col items-center justify-center gap-2" 
                                    style={{ 
                                      backgroundColor: offer.offer_category === 'festive' ? '#FF6B35' : 
                                                       offer.offer_category === 'loyalty' ? '#9C27B0' :
                                                       offer.offer_category === 'welcome' ? '#00BCD4' :
                                                       '#4CAF50'
                                    }}
                                  >
                                    {/* Decorative Circles */}
                                    <div className="w-2 h-2 rounded-full bg-white opacity-60"></div>
                                    <div className="w-2 h-2 rounded-full bg-white opacity-60"></div>
                                    <div className="w-2 h-2 rounded-full bg-white opacity-60"></div>
                                    <div className="w-2 h-2 rounded-full bg-white opacity-60"></div>
                                  </div>

                                  <div className="pl-14 pr-3 py-3 flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      {/* Title */}
                                      <div className="flex items-center gap-1.5 mb-1.5">
                                        <span className="text-base">
                                          {offer.offer_category === 'festive' ? '🎊' : 
                                           offer.offer_category === 'loyalty' ? '⭐' :
                                           offer.offer_category === 'welcome' ? '👋' :
                                           offer.offer_category === 'seasonal' ? '🌸' :
                                           '🎁'}
                                        </span>
                                        <h4 className="font-semibold text-sm" style={{ color: DARK_TEXT }}>
                                          {offer.display_title || offer.offer_name}
                                        </h4>
                                      </div>

                                      {/* Discount Description */}
                                      <p className="text-xs mb-2 font-medium" style={{ color: '#00897B' }}>
                                        {offer.display_subtitle || 
                                         `Get ${offer.discount_type === 'percentage' ? `${offer.discount_value}% off` : `₹${offer.discount_value} off`}`}
                                      </p>

                                      {/* Dotted Divider */}
                                      <div className="border-t border-dashed border-gray-300 mb-2"></div>

                                      {/* Conditions */}
                                      {offer.min_order_amount > 0 && (
                                        <p className="text-[10px] mb-0.5" style={{ color: '#9E9E9E' }}>
                                          Applicable on Order above ₹{offer.min_order_amount}
                                        </p>
                                      )}
                                      {offer.max_discount_amount && (
                                        <p className="text-[10px] mb-2" style={{ color: '#9E9E9E' }}>
                                          Max ₹{offer.max_discount_amount}
                                        </p>
                                      )}

                                      {/* Status Messages */}
                                      {offer.min_order_amount && subtotal < offer.min_order_amount && (
                                        <p className="text-[10px] text-red-600 font-semibold">
                                          ⚠️ Minimum order ₹{offer.min_order_amount} required
                                        </p>
                                      )}
                                      {isApplied && (
                                        <p className="text-[10px] font-semibold" style={{ color: '#15803D' }}>
                                          ✓ Saving ₹{offerDiscount.toFixed(2)}
                                        </p>
                                      )}
                                    </div>

                                    {/* Apply Button */}
                                    <button
                                      onClick={() => {
                                        if (isApplied) {
                                          // Remove offer
                                          setAppliedOffer(null)
                                          setUseOrdyrrCoins(false)
                                          toast.success('Offer removed')
                                        } else {
                                          // Apply offer
                                          setAppliedOffer(offer)
                                          setUseOrdyrrCoins(false) // Can't use both
                                          toast.success(`${offer.offer_code} applied!`)
                                          // Show sparkles animation
                                          setShowSparkles(true)
                                          setTimeout(() => setShowSparkles(false), 1500)
                                          // Close the coupon section after applying
                                          setTimeout(() => setShowCouponSection(false), 300)
                                        }
                                      }}
                                      disabled={offer.min_order_amount && subtotal < offer.min_order_amount}
                                      className="px-4 py-1 text-xs font-bold rounded transition-all flex-shrink-0"
                                      style={{ 
                                        color: isApplied ? '#15803D' : '#4CAF50',
                                        backgroundColor: 'transparent',
                                        opacity: (offer.min_order_amount && subtotal < offer.min_order_amount) ? 0.5 : 1
                                      }}
                                    >
                                      {isApplied ? 'APPLIED' : 'APPLY'}
                                    </button>
                                  </div>
                                </div>
                              )
                            })}

                            {/* Loading State */}
                            {loadingOffers && (
                              <div className="text-center py-4">
                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
                                <p className="text-xs mt-2" style={{ color: MEDIUM_GRAY }}>Loading offers...</p>
                              </div>
                            )}

                            {/* Guest User Signup Prompt */}
                            {!isAuthenticated && (
                              <div className="border-l-4 pl-3 pr-2 py-3 rounded-r-lg" style={{ borderColor: '#F59E0B', backgroundColor: '#FEF3C7' }}>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-2xl">🎁</span>
                                  <h4 className="font-bold text-sm" style={{ color: DARK_TEXT }}>Sign up for exclusive offers!</h4>
                                </div>
                                <p className="text-xs mb-3" style={{ color: '#92400E' }}>
                                  Get 100 Ordyrr Coins + 10% off on your first order
                                </p>
                                <button
                                  onClick={onClose}
                                  className="w-full py-2 text-xs font-bold text-black rounded-lg"
                                  style={{ 
                                    backgroundColor: '#F59E0B',
                                    boxShadow: '0 3px 0 0 #000000'
                                  }}
                                >
                                  CREATE ACCOUNT
                                </button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Review Items Section */}
                  <div className="mx-4 mt-4">
                    <h3 className="font-bold text-base mb-3" style={{ color: DARK_TEXT }}>Review Items</h3>
                    <div className="bg-white rounded-xl p-4 space-y-4" style={{ boxShadow: '0px 2px 8px rgba(0,0,0,0.08)' }}>
                      {cart.map((item) => (
                        <div key={item.id} className="flex items-start gap-3">
                          {/* Item Image with Veg Indicator */}
                          <div className="relative flex-shrink-0">
                            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-2xl">🍽️</span>
                              )}
                            </div>
                            {/* Veg/Non-veg Badge */}
                            <div className="absolute -top-1 -left-1 w-4 h-4 rounded-sm border-2 flex items-center justify-center bg-white" style={{ borderColor: '#4CAF50' }}>
                              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#4CAF50' }}></div>
                            </div>
                          </div>
                          
                          {/* Item Details */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm mb-0.5" style={{ color: DARK_TEXT }}>{item.name}</h4>
                            <p className="text-xs" style={{ color: MEDIUM_GRAY }}>
                              300 ml x {item.quantity}
                            </p>
                          </div>
                          
                          {/* Price and Quantity Controls */}
                          <div className="text-right flex-shrink-0 flex flex-col items-end gap-2">
                            {/* Price */}
                            <div>
                              {item.original_price && item.original_price > item.price && (
                                <p className="text-xs line-through mb-0.5" style={{ color: '#BDBDBD' }}>₹{(item.original_price * item.quantity).toFixed(0)}</p>
                              )}
                              <p className="font-bold text-base" style={{ color: DARK_TEXT }}>₹{(item.price * item.quantity).toFixed(0)}</p>
                            </div>
                            
                            {/* Quantity Controls */}
                            <div 
                              className="inline-flex items-center gap-3 px-3 py-1 rounded-lg"
                              style={{ 
                                border: '1.5px solid #E0E0E0',
                                backgroundColor: 'white'
                              }}
                            >
                              <button
                                onClick={() => updateQuantity(item.id, Math.max(0, item.quantity - 1))}
                                className="font-bold text-base leading-none"
                                style={{ color: ACTION_GREEN }}
                              >
                                −
                              </button>
                              <span className="font-bold text-sm min-w-[16px] text-center" style={{ color: ACTION_GREEN }}>{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="font-bold text-base leading-none"
                                style={{ color: ACTION_GREEN }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Add More Items Button - Inside Card */}
                      <div className="px-4 pb-3 pt-2">
                        <button
                          onClick={onClose}
                          className="w-full text-center py-2 font-bold text-sm"
                          style={{ color: ACTION_GREEN }}
                        >
                          + Add more items
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Savings Banner - Show how much more to add for next offer */}
                  {(() => {
                    const currentSubtotal = subtotal
                    let nextThreshold = 0
                    let savingsAmount = 0
                    let offerType = ''
                    
                    // Determine next offer threshold
                    if (currentSubtotal < 299) {
                      nextThreshold = 299
                      savingsAmount = 25
                      offerType = 'FIRST_ORDER_25'
                    } else if (currentSubtotal >= 299 && currentSubtotal < 499) {
                      nextThreshold = 499
                      savingsAmount = 50
                      offerType = 'FIRST_ORDER_10'
                    }
                    
                    const amountNeeded = nextThreshold - currentSubtotal
                    
                    // Only show banner if user is close to next threshold and hasn't placed first order
                    if (amountNeeded > 0 && amountNeeded <= 200 && isAuthenticated && !hasUsedFirstOrderDiscount) {
                      return (
                        <div className="px-4 mt-4 mb-2">
                          <div 
                            className="rounded-xl p-3.5 border"
                            style={{ 
                              backgroundColor: '#FFFFFF',
                              borderColor: '#E0E0E0'
                            }}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFF3E0' }}>
                                <span className="text-base">🎯</span>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-bold mb-0.5" style={{ color: DARK_TEXT }}>
                                  Add ₹{amountNeeded.toFixed(0)} more to save ₹{savingsAmount}!
                                </p>
                                <p className="text-xs" style={{ color: MEDIUM_GRAY }}>
                                  {offerType === 'FIRST_ORDER_25' 
                                    ? 'Get flat ₹25 off on orders above ₹299' 
                                    : 'Get 10% off (max ₹50) on orders above ₹499'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    }
                    return null
                  })()}

                  {/* You Might Also Like - Horizontal Scrolling */}
                  {suggestedItems.length > 0 && (
                  <div className="border-t mt-4">
                    <h4 className="px-4 pt-4 mb-3 font-black italic uppercase text-base" style={{ color: DARK_TEXT }}>
                      You Might Like This<span style={{ color: ACTION_GREEN }}>.</span>
                    </h4>
                    <div className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-4">
                      {suggestedItems.map((item) => (
                        <div 
                          key={item.id} 
                          className="bg-white rounded-2xl overflow-hidden flex-shrink-0"
                          style={{ boxShadow: '0px 2px 8px rgba(0,0,0,0.08)', width: '160px' }}
                        >
                          {/* Food Image */}
                          <div className="relative bg-gray-100" style={{ height: '110px', borderRadius: '16px 16px 0 0' }}>
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className="text-4xl">🍽️</span>
                              </div>
                            )}
                          </div>

                          {/* Card Content */}
                          <div className="p-2.5">
                            <div className="flex items-center gap-1 mb-1.5">
                              <div className="w-3.5 h-3.5 rounded-sm border-2 flex items-center justify-center flex-shrink-0" style={{ borderColor: '#4CAF50' }}>
                                <div className="w-1 h-1 rounded-full" style={{ backgroundColor: '#4CAF50' }}></div>
                              </div>
                              <span className="text-[10px] font-medium" style={{ color: DARK_TEXT }}>⏱ 16 mins</span>
                              <span className="text-[10px] font-medium" style={{ color: MEDIUM_GRAY }}>2 cups</span>
                            </div>

                            <h5 className="font-bold text-xs mb-1.5 line-clamp-2" style={{ color: DARK_TEXT }}>{item.name}</h5>

                            <div className="flex items-center justify-between">
                              <p className="font-bold text-sm" style={{ color: DARK_TEXT }}>₹{item.price}</p>
                              <button
                                onClick={() => addToCart(item)}
                                className="px-3 py-1 text-xs font-bold text-black"
                                style={{ 
                                  backgroundColor: '#00C853',
                                  borderRadius: '6px',
                                  boxShadow: '0 2px 0 0 #000000'
                                }}
                              >
                                ADD
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                </>
              )}
            </div>

            {/* Checkout Section - Sticky Bottom */}
            {cart.length > 0 && (
              <div className="bg-white border-t border-gray-200">
                {/* Bill Summary - Collapsible */}
                <div className="px-4 pt-3">
                  <button
                    onClick={() => setShowBillDetails(!showBillDetails)}
                    className="w-full flex items-center justify-between mb-3"
                  >
                    <div className="flex flex-col items-start gap-1">
                      {/* Show strikethrough original price if there are savings */}
                      {itemSavings > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="relative inline-block"
                        >
                          <span className="text-sm font-semibold" style={{ color: MEDIUM_GRAY }}>
                            ₹{originalTotal.toFixed(2)}
                          </span>
                          {/* Animated strikethrough line */}
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: '100%' }}
                            transition={{ 
                              duration: 0.6, 
                              delay: 0.3,
                              ease: [0.43, 0.13, 0.23, 0.96] // Custom easing for smooth effect
                            }}
                            className="absolute top-1/2 left-0 h-[2px] -translate-y-1/2"
                            style={{ 
                              backgroundColor: '#BDBDBD',
                              transformOrigin: 'left'
                            }}
                          />
                        </motion.div>
                      )}
                      {/* Current discounted price */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, delay: itemSavings > 0 ? 0.2 : 0 }}
                        className="flex items-center gap-2"
                      >
                        <span className="font-black text-lg" style={{ color: DARK_TEXT }}>
                          To Pay: ₹{total.toFixed(2)}
                        </span>
                        {itemSavings > 0 && (
                          <motion.span
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: 0.4 }}
                            className="text-xs font-bold px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
                          >
                            Save ₹{itemSavings.toFixed(0)}
                          </motion.span>
                        )}
                      </motion.div>
                    </div>
                    <button className="text-orange-600 text-xs font-semibold flex items-center gap-1">
                      View Detailed Bill
                      {showBillDetails ? (
                        <ChevronUpIcon className="w-4 h-4" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </button>
                  </button>

                  {/* Expandable Bill Details */}
                  <AnimatePresence>
                    {showBillDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 mb-4 pb-4 border-b border-gray-200">
                          <div className="flex justify-between text-sm" style={{ color: DARK_TEXT }}>
                            <span>Item Total</span>
                            <span>₹{subtotal.toFixed(0)}</span>
                          </div>
                          {itemSavings > 0 && (
                            <div className="flex justify-between text-sm font-bold" style={{ color: '#00C853' }}>
                              <span>💰 Savings with Ordyrr</span>
                              <span>-₹{itemSavings.toFixed(0)}</span>
                            </div>
                          )}
                          {discount > 0 && (
                            <div className="flex justify-between text-sm" style={{ color: '#15803D' }}>
                              <span>First Order Discount</span>
                              <span>-₹{discount.toFixed(0)}</span>
                            </div>
                          )}
                          {coinsDiscount > 0 && (
                            <div className="flex justify-between text-sm" style={{ color: '#F59E0B' }}>
                              <span>Ordyrr Coins ({Math.floor(coinsUsed)})</span>
                              <span>-₹{coinsDiscount.toFixed(2)}</span>
                            </div>
                          )}

                          {tipAmount > 0 && (
                            <div className="flex justify-between text-sm" style={{ color: MEDIUM_GRAY }}>
                              <span>Tip for Staff</span>
                              <span style={{ color: DARK_TEXT }}>₹{tipAmount}</span>
                            </div>
                          )}
                        </div>

                        {/* Tip Section */}
                        <div className="mb-4">
                          <h4 className="text-xs font-bold mb-2" style={{ color: DARK_TEXT }}>
                            💝 Add Tip for Staff
                          </h4>
                          <div className="flex gap-2 mb-2">
                            {[10, 20, 50].map((amount) => (
                              <button
                                key={amount}
                                onClick={() => {
                                  setTipAmount(amount)
                                  setShowCustomTip(false)
                                  setCustomTip('')
                                }}
                                className="flex-1 py-2 text-xs font-bold rounded-lg transition-all"
                                style={
                                  tipAmount === amount && !showCustomTip
                                    ? { backgroundColor: ACTION_GREEN, color: '#000' }
                                    : { backgroundColor: '#F3F4F6', color: '#374151' }
                                }
                              >
                                ₹{amount}
                              </button>
                            ))}
                            <button
                              onClick={() => {
                                setShowCustomTip(true)
                                setTipAmount(0)
                              }}
                              className="flex-1 py-2 text-xs font-bold rounded-lg transition-all"
                              style={
                                showCustomTip
                                  ? { backgroundColor: ACTION_GREEN, color: '#000' }
                                  : { backgroundColor: '#F3F4F6', color: '#374151' }
                              }
                            >
                              Custom
                            </button>
                          </div>
                          
                          {showCustomTip && (
                            <div className="flex gap-2">
                              <input
                                type="number"
                                value={customTip}
                                onChange={(e) => setCustomTip(e.target.value)}
                                placeholder="Enter amount"
                                className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:border-green-600"
                              />
                              <button
                                onClick={() => {
                                  const amount = parseInt(customTip)
                                  if (amount > 0) {
                                    setTipAmount(amount)
                                  }
                                }}
                                className="px-4 py-2 text-xs font-bold text-black rounded-lg"
                                style={{ backgroundColor: ACTION_GREEN }}
                              >
                                Add
                              </button>
                            </div>
                          )}
                          
                          {tipAmount > 0 && !showCustomTip && (
                            <p className="text-[9px] font-medium mt-1" style={{ color: '#15803D' }}>
                              ✓ Tip of ₹{tipAmount} will be added to your bill
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Proceed to Pay Button */}
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onCheckout(tipAmount, useOrdyrrCoins, appliedOffer)}
                    className="w-full text-black py-4 font-black text-base uppercase rounded-xl mb-3"
                    style={{ 
                      backgroundColor: ACTION_GREEN,
                      boxShadow: '0 4px 0 0 #000000'
                    }}
                  >
                    Proceed to Pay
                  </motion.button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Celebratory Discount Popup - Minimal Design */}
          <AnimatePresence>
            {showDiscountPopup && discount > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm"
                onClick={() => setShowDiscountPopup(false)}
              >
                {/* Outer Sparkles - Burst Effect */}
                {[...Array(30)].map((_, i) => {
                  const angle = (i / 30) * 360
                  const distance = 150 + Math.random() * 100
                  return (
                    <motion.div
                      key={i}
                      initial={{ 
                        x: 0, 
                        y: 0, 
                        scale: 0, 
                        opacity: 0,
                        rotate: 0
                      }}
                      animate={{
                        x: Math.cos(angle * Math.PI / 180) * distance,
                        y: Math.sin(angle * Math.PI / 180) * distance,
                        scale: [0, 1.5, 1],
                        opacity: [0, 1, 0],
                        rotate: [0, 360]
                      }}
                      transition={{
                        duration: 1.5,
                        delay: i * 0.02,
                        ease: "easeOut"
                      }}
                      className="absolute"
                      style={{ pointerEvents: 'none' }}
                    >
                      <span className="text-yellow-400 text-2xl">✨</span>
                    </motion.div>
                  )
                })}

                {/* Popup Card - Redesigned with Ordyrr Brand Style */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.8, opacity: 0, y: 20 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="bg-white rounded-3xl p-8 max-w-sm w-full mx-4 relative z-10"
                  style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Close Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (popupTimeout.current) {
                        clearTimeout(popupTimeout.current)
                      }
                      setShowDiscountPopup(false)
                    }}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-600" />
                  </button>

                  {/* Content */}
                  <div className="text-center pt-2">
                    {/* Animated Celebration Icon with Stars */}
                    <div className="relative mb-6">
                      {/* Left Star */}
                      <motion.div
                        animate={{ 
                          rotate: [0, 10, -10, 0],
                          scale: [1, 1.2, 1]
                        }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute -left-4 top-2"
                      >
                        <span className="text-2xl">⭐</span>
                      </motion.div>
                      
                      {/* Right Star */}
                      <motion.div
                        animate={{ 
                          rotate: [0, -10, 10, 0],
                          scale: [1, 1.2, 1]
                        }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                        className="absolute -right-4 top-2"
                      >
                        <span className="text-2xl">⭐</span>
                      </motion.div>

                      {/* Center Celebration Icon */}
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', delay: 0.1, duration: 0.8 }}
                        className="text-7xl mb-2"
                      >
                        🎉
                      </motion.div>
                    </div>
                    
                    {/* Congratulations Text */}
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="text-base font-semibold mb-3"
                      style={{ color: MEDIUM_GRAY }}
                    >
                      Congratulations!
                    </motion.p>
                    
                    {/* Main Message */}
                    <motion.h3
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="text-3xl font-black mb-1"
                      style={{ color: DARK_TEXT }}
                    >
                      Instant ₹{discount.toFixed(0)} off!
                    </motion.h3>
                    
                    {/* Subtitle */}
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="text-xs font-medium mb-6 uppercase tracking-wide"
                      style={{ color: MEDIUM_GRAY }}
                    >
                      First order discount applied
                    </motion.p>
                    
                    {/* CTA Button - Ordyrr Green */}
                    <motion.button
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (popupTimeout.current) {
                          clearTimeout(popupTimeout.current)
                        }
                        setShowDiscountPopup(false)
                      }}
                      className="w-full py-4 font-black text-white text-base uppercase rounded-2xl transition-all hover:opacity-90"
                      style={{ 
                        backgroundColor: BRAND_GREEN,
                        boxShadow: '0 4px 14px rgba(0, 230, 118, 0.4)'
                      }}
                    >
                      YAY!
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  )
}

export default CartSidebar
